import { TenantMinimalDto, TenantSeniorityStatusEnum } from "api/real-estate";
import { ReferentialKey } from "api/referential";
import { Button } from "components/ui/Button";
import { LightAction } from "components/ui/LightAction";
import { MessagesTooltip } from "components/feedback/MessagesTooltip";
import { ExclamationPointIcon, ManCry } from "components/icons/customIcons";
import { AddIcon, CheckIcon, DeleteIcon, LogoutIcon, PenIcon } from "components/icons/Icons";
import { getCivilityIcon } from "shared/utils/civility";
import { withPersonName } from "shared/utils/panelTitle";
import { Sizes } from "components/forms/NumberSelector";
import { NsKey } from "enums/NsKey";
import { selectReferentialDataByKey } from "features/referentials/selectors/selectReferentialDataByKey";
import { SubscriptionProcessContext, useSubscriptionProcess } from "features/subscription-process/contexts/SubscriptionProcessContext";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { usePanel } from "hooks/usePanel";
import { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTenantFormActions } from "../../hooks/useTenantFormActions";
import { TenantForm } from "./TenantForm";
import { TenantGuarantorTooltip } from "./TenantGuarantorTooltip";

interface TenantManagerProps {
  riskId?: string
  tenants: TenantMinimalDto[]
  loadTenants: () => void
  /** Bien cible explicite (tunnel parc) — par défaut le bien du risque mono-bien. */
  realEstateId?: string
}

export const TenantManager: FC<TenantManagerProps> = ({ loadTenants, tenants, riskId, realEstateId }) => {
  const { openPanel } = usePanel()
  const { t } = useDynamicTranslation([NsKey.RealEstate])

  const contextValue = useSubscriptionProcess()

  const civilityReferential = useSelector(selectReferentialDataByKey(ReferentialKey.Civility))
  const employmentReferential = useSelector(selectReferentialDataByKey(ReferentialKey.EmploymentStatus))

  const getGenderIcon = useCallback((civilityId: string) => {
    const ICON_CLASS = "w-5 text-primary"
    const civilityRef = civilityReferential.find(ref => ref.id === civilityId)
    const Icon = getCivilityIcon(civilityRef?.code)
    return <Icon className={ICON_CLASS} />
  }, [civilityReferential])

  const getProfessionnalSituation = useCallback((id: string) => {
    const ref = employmentReferential.find(ref => ref.id === id)
    return ref && ref.label
  }, [employmentReferential])

  const { handleDeleteWithConfirmation, handleTenantCheckOut } = useTenantFormActions({ refreshTenants: loadTenants })

  /** Ancienneté du locataire au début du contrat, affichée à la place des revenus. */
  const seniorityInfo = useCallback((tenant: TenantMinimalDto) => {
    switch (tenant.seniorityStatus) {
      case TenantSeniorityStatusEnum.InPlace:
        if (tenant.rentPaymentsUpToDate === true) {
          return <span className="text-primary">À jour de ses loyers</span>
        }
        if (tenant.rentPaymentsUpToDate === false) {
          return <span className="text-secondary">Loyers impayés</span>
        }
        return <span className="text-secondary">Loyers à déclarer</span>
      case TenantSeniorityStatusEnum.RefusedPeriod:
        return <span className="text-secondary">Refusé (16 j – 6 mois)</span>
      case TenantSeniorityStatusEnum.FutureEntry:
        return <span className="text-secondary">À souscrire plus tard (entrée &gt; 6 mois)</span>
      default:
        return <span className="text-gray-700">Nouveau locataire</span>
    }
  }, [])

  const rentToIncomeRatio = useCallback((tenantRatio: number, guarantorRatio?: number) => {
    if (guarantorRatio) {
      return [
        `Etudiant : ${(tenantRatio * 100).toFixed(1)}%`,
        `Garant : ${(guarantorRatio * 100).toFixed(1)}%`
      ]
    }
    return [`Locataire : ${(tenantRatio * 100).toFixed(1)}%`]
  }, [])

  const addTenant = useCallback(() => {
    openPanel({
      title: t('REAL_ESTATE.LABELS.ADD_TENANT'),
      size: Sizes["2xl"],
    },
      <SubscriptionProcessContext.Provider value={contextValue}>
        <TenantForm riskId={riskId} realEstateId={realEstateId} refreshTenants={loadTenants} />
      </SubscriptionProcessContext.Provider>
    )
  }, [openPanel, t, contextValue, loadTenants, riskId, realEstateId])

  const editTenant = useCallback((tenantId: string) => {
    openPanel({
      title: withPersonName(t('REAL_ESTATE.LABELS.EDIT_TENANT'), tenants.find(tenant => tenant.id === tenantId)?.fullName),
      size: Sizes["2xl"],
    },
      <SubscriptionProcessContext.Provider value={contextValue}>
        <TenantForm riskId={riskId} realEstateId={realEstateId} tenantId={tenantId} refreshTenants={loadTenants} />
      </SubscriptionProcessContext.Provider>
    )
  }, [openPanel, t, contextValue, loadTenants, riskId, realEstateId, tenants])

  if (tenants.length === 0) {
    return <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('REAL_ESTATE.LABELS.TENANTS')}</h3>
      <div className="flex flex-col items-center justify-center py-12 text-center rounded-xl border bg-white">
        <ManCry className="h-16 w-16 text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t("TENANT.LABELS.EMPTY_TENANTS_TITLE")}
        </h3>
        <p className="text-gray-500 mb-6 max-w-md">
          {t("TENANT.LABELS.EMPTY_TENANTS_DESCRIPTION")}
        </p>
        <LightAction
          icon={AddIcon}
          label={t("REAL_ESTATE.LABELS.ADD_TENANT")}
          onClick={addTenant}
        />
      </div>
    </div>
  }

  return <div className="space-y-4">
    <h3 className="text-lg font-semibold">{t('REAL_ESTATE.LABELS.TENANTS')}</h3>
    <div className="space-y-2">
      {
        tenants.map((tenant, index) => (<div
          key={`${tenant.id}-${index}`}
          className="border flex items-center text-sm rounded-2xl bg-white gap-4 px-4 py-2 shadow"
        >
          <div className="grid grid-cols-12 divide-x w-full">
            <div className="col-span-4 flex items-center gap-4">
              {tenant.civilityId && getGenderIcon(tenant.civilityId)}
              {tenant.fullName}
            </div>
            <div className={`col-span-4 gap-2 px-4 flex items-center ${tenant.isSolvable ? 'text-primary' : 'text-secondary'}`}>
              {
                tenant.isSolvable ? (
                  <MessagesTooltip
                    sections={[
                      {
                        title: "Taux d'effort",
                        type: "info",
                        messages: rentToIncomeRatio(
                          tenant.rentToIncomeRatio,
                          tenant.guarantor?.rentToIncomeRatio
                        ),
                      },
                    ]}
                  >
                    <div className="p-1 rounded-full bg-lime-500 text-white">
                      <CheckIcon className="w-3" />
                    </div>
                  </MessagesTooltip>
                ) : (
                  <MessagesTooltip
                    sections={[
                      {
                        title: "Raison d'inégibilité",
                        type: "warning",
                        messages: tenant.warningMessages || [],
                      },
                    ]}
                  >
                    <div className="p-1 rounded-full bg-orange-500 text-white">
                      <ExclamationPointIcon className="w-3" />
                    </div>
                  </MessagesTooltip>
                )
              }
              {tenant.isSolvable ? 'Eligible à la GLI' : 'Non éligible à la GLI'}
            </div>
            <div className="col-span-4 gap-2 px-4 flex items-center">
              {
                tenant.guarantor &&
                <TenantGuarantorTooltip
                  fullName={tenant.guarantor.fullName || ''}
                  professionnalSituation={getProfessionnalSituation(tenant.guarantor.employmentStatusId || '') || ''}
                />
              }
              {seniorityInfo(tenant)}
            </div>
          </div>
          <div className="flex gap-2 items-center justify-end">
            <Button
              round
              color='slate'
              onClick={() => tenant.id && handleTenantCheckOut(tenant.id)}
            >
              <LogoutIcon className="w-5 h-5" />
            </Button>
            <Button
              round
              color='slate'
              onClick={() => tenant.id && handleDeleteWithConfirmation(tenant.id)}
            >
              <DeleteIcon className="w-5 h-5" />
            </Button>
            <Button
              round
              color='slate'
              onClick={() => tenant.id && editTenant(tenant.id)}
            >
              <PenIcon className="w-5 h-5" />
            </Button>
          </div>
        </div>
        )
        )
      }
    </div>
    <button className="w-full flex justify-center bg-white hover:bg-primary/5 text-primary gap-4 border-2 border-primary/20 hover:border-primary/50 rounded-md border-dashed p-2"
      onClick={addTenant}
    >
      <AddIcon className="w-4" />
      {t('REAL_ESTATE.LABELS.ADD_TENANT')}
    </button>
  </div>
}
